import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  uploadAttachment,
  useActivities,
  useActivityTypes,
  useAddActivity,
  useAttachments,
  useCustomFields,
  useDeleteActivity,
  useDeleteLead,
  useLeadAudit,
  useLeads,
  useMembers,
  useStages,
  useTags,
  useUpdateLead,
} from '../api/queries';
import type { CustomField, Lead, Stage } from '@figureit/shared';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  daysSince,
  fmtDate,
  fmtILS,
  isOverdue,
  telHref,
  whatsappHref,
} from '../utils/format';
import { LogActivitySheet } from './LogActivitySheet';
import { WonLostPrompt } from './WonLostPrompt';

type LogSheetState = { typeName?: string; title?: string };
type ContactKind = 'Call' | 'WhatsApp' | 'Email';

/** How long the app must be in the background before we assume the call happened. */
const CONTACT_PROMPT_MIN_MS = 8_000;

export function LeadDetail({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const leadsQ = useLeads();
  const lead = leadsQ.data?.items.find((l) => l.id === leadId);
  if (!lead) return null;
  return <LeadDetailContent lead={lead} onClose={onClose} />;
}

function LeadDetailContent({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const stages = useStages().data?.items ?? [];
  const cfs = useCustomFields().data?.items ?? [];
  const types = useActivityTypes().data?.items ?? [];
  const tagsAll = useTags().data?.items ?? [];
  const members = useMembers().data?.items ?? [];

  const update = useUpdateLead();
  const remove = useDeleteLead();
  const addAct = useAddActivity(lead.id);
  const delAct = useDeleteActivity(lead.id);

  const acts = useActivities(lead.id).data ?? [];
  const audit = useLeadAudit(lead.id).data?.items ?? [];
  const atts = useAttachments(lead.id).data?.items ?? [];

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Lead>(lead);
  useEffect(() => setForm(lead), [lead]);

  // Esc closes the panel (desktop nicety; harmless on phones).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, onClose]);

  const [pendingTerminal, setPendingTerminal] = useState<Stage | null>(null);
  // The "what happened?" sheet. Opened from the quick-log chips, "+ Add
  // activity", or automatically when the rep comes back from a call.
  const [logSheet, setLogSheet] = useState<LogSheetState | null>(null);

  // After-contact prompt: when the rep taps Call/WhatsApp/Email the phone
  // switches apps. When they come back (page visible again after a real
  // absence) we open the log sheet pre-set to that type, so the call gets
  // documented while it is still fresh.
  const pendingContact = useRef<{ kind: ContactKind; at: number } | null>(null);
  const onContact = (kind: ContactKind) => {
    pendingContact.current = { kind, at: Date.now() };
  };
  useEffect(() => {
    const check = () => {
      const p = pendingContact.current;
      if (!p || document.visibilityState !== 'visible') return;
      if (Date.now() - p.at < CONTACT_PROMPT_MIN_MS) return;
      pendingContact.current = null;
      if (editing) return;
      const question =
        p.kind === 'Call'
          ? 'How did the call go?'
          : p.kind === 'WhatsApp'
            ? 'Log the WhatsApp chat'
            : 'Log the email';
      setLogSheet({ typeName: p.kind, title: question });
    };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('pageshow', check);
    window.addEventListener('focus', check);
    return () => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('pageshow', check);
      window.removeEventListener('focus', check);
    };
  }, [editing]);

  const sortedCfs = useMemo(
    () => cfs.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [cfs],
  );
  const sortedStages = useMemo(() => stages.slice().sort((a, b) => a.order - b.order), [stages]);

  const stage = stages.find((s) => s.id === lead.stageId);
  const owner = members.find((m) => m.userId === lead.ownerUserId);

  const setCF = (fieldId: string, v: unknown) =>
    setForm((f) => ({ ...f, customFields: { ...(f.customFields ?? {}), [fieldId]: v as never } }));

  const saveEdit = () => {
    const { id, createdAt, lastActivityAt, deletedAt, position, ...payload } = form;
    update.mutate({ id, ...payload });
    setEditing(false);
  };
  const cancelEdit = () => {
    setForm(lead);
    setEditing(false);
  };

  // Stage change straight from read mode — the main "on the go" update. Won/Lost
  // go through the reason prompt, which owns the mutation.
  const changeStage = (stageId: string) => {
    const next = stages.find((s) => s.id === stageId);
    if (!next || next.id === lead.stageId) return;
    if (next.isWon || next.isLost) {
      setPendingTerminal(next);
      return;
    }
    update.mutate({ id: lead.id, stageId: next.id });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex justify-end animate-[fadeIn_0.15s_ease]"
      onClick={onClose}
    >
      <div
        className="w-full md:w-[640px] md:max-w-full h-full bg-surface md:border-l border-border flex flex-col animate-[slideIn_0.2s_ease] overscroll-contain"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* ---- Sticky header ---- */}
        <div className="flex-shrink-0 flex items-center gap-2 px-3 md:px-6 pt-[max(0.5rem,env(safe-area-inset-top))] md:pt-5 pb-2.5 md:pb-4 border-b border-border bg-surface">
          <button
            className="btn btn-ghost px-2 -ml-1 text-2xl leading-none flex-shrink-0"
            onClick={editing ? cancelEdit : onClose}
            aria-label={editing ? 'Cancel editing' : 'Close'}
          >
            <span className="md:hidden">‹</span>
            <span className="hidden md:inline">×</span>
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="text-base md:text-xl font-bold truncate leading-tight">
              {lead.company}
            </h2>
            <div className="text-text2 text-xs md:text-sm truncate">
              {lead.contactName}
              {lead.contactTitle ? ` · ${lead.contactTitle}` : ''}
            </div>
          </div>
          <div className="flex gap-1.5 flex-shrink-0">
            {!editing ? (
              <button className="btn btn-sm btn-secondary" onClick={() => setEditing(true)}>
                Edit
              </button>
            ) : (
              <>
                <button className="btn btn-sm btn-ghost hidden md:inline-flex" onClick={cancelEdit}>
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={saveEdit}
                  disabled={update.isPending}
                >
                  {update.isPending ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* ---- Scrollable body ---- */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 md:px-6 py-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          {!editing && (
            <>
              <ContactActions lead={lead} onContact={onContact} />
              <StageBar
                stage={stage}
                stages={sortedStages}
                stageEnteredAt={lead.stageEnteredAt}
                onChange={changeStage}
                busy={update.isPending}
              />
              <QuickLogBar
                types={types}
                onLog={(typeName) => setLogSheet({ typeName })}
                disabled={addAct.isPending}
              />
            </>
          )}

          <Section title="Lead details">
            {editing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Company">
                  <input
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    autoCapitalize="words"
                    enterKeyHint="next"
                  />
                </Field>
                <Field label="Contact name">
                  <input
                    value={form.contactName ?? ''}
                    onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                    autoCapitalize="words"
                    autoComplete="off"
                    enterKeyHint="next"
                  />
                </Field>
                <Field label="Title">
                  <input
                    value={form.contactTitle ?? ''}
                    onChange={(e) => setForm({ ...form, contactTitle: e.target.value })}
                    enterKeyHint="next"
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    value={form.email ?? ''}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    enterKeyHint="next"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    type="tel"
                    inputMode="tel"
                    value={form.phone ?? ''}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    enterKeyHint="next"
                  />
                </Field>
                <Field label="Source">
                  <input
                    value={form.source ?? ''}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                    enterKeyHint="next"
                  />
                </Field>
                <Field label="Stage">
                  <select
                    value={form.stageId}
                    onChange={(e) => {
                      const next = stages.find((s) => s.id === e.target.value);
                      if (!next) return;
                      if ((next.isWon || next.isLost) && next.id !== lead.stageId) {
                        // Defer the change to the prompt, then close the editor.
                        setPendingTerminal(next);
                        return;
                      }
                      setForm({ ...form, stageId: next.id });
                    }}
                  >
                    {sortedStages.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="NRE Value (₪)">
                  <input
                    type="number"
                    inputMode="decimal"
                    value={form.nreValue ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        nreValue: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </Field>
                <Field label="Deal Value (₪/yr)">
                  <input
                    type="number"
                    inputMode="decimal"
                    value={form.dealValueYearly ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        dealValueYearly: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </Field>
                <Field label="Priority">
                  <select
                    value={form.priority}
                    onChange={(e) =>
                      setForm({ ...form, priority: e.target.value as 'A' | 'B' | 'C' })
                    }
                  >
                    <option value="A">A — High</option>
                    <option value="B">B — Medium</option>
                    <option value="C">C — Low</option>
                  </select>
                </Field>
                <Field label="Owner">
                  <select
                    value={form.ownerUserId ?? ''}
                    onChange={(e) => setForm({ ...form, ownerUserId: e.target.value || null })}
                  >
                    <option value="">Unassigned</option>
                    {members.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name ?? m.email}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Next Action">
                  <input
                    value={form.nextAction ?? ''}
                    onChange={(e) => setForm({ ...form, nextAction: e.target.value })}
                    enterKeyHint="next"
                  />
                </Field>
                <Field label="Next Action Date">
                  <input
                    type="date"
                    value={form.nextActionDate ?? ''}
                    onChange={(e) => setForm({ ...form, nextActionDate: e.target.value || null })}
                  />
                </Field>
                {sortedCfs.map((f) => (
                  <CustomFieldEditor
                    key={f.id}
                    field={f}
                    value={(form.customFields ?? {})[f.id] ?? ''}
                    onChange={(v) => setCF(f.id, v)}
                  />
                ))}
                <Field label="Notes" full>
                  <textarea
                    rows={4}
                    value={form.notes ?? ''}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </Field>
                <Field label="Tags" full>
                  <TagPicker
                    available={tagsAll}
                    selected={form.tagIds ?? []}
                    onChange={(tagIds) => setForm({ ...form, tagIds })}
                  />
                </Field>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                <ReadField
                  label="Email"
                  value={
                    lead.email ? (
                      <a className="text-accent2 break-all" href={`mailto:${lead.email}`}>
                        {lead.email}
                      </a>
                    ) : null
                  }
                />
                <ReadField
                  label="Phone"
                  value={
                    lead.phone ? (
                      <a className="text-accent2" href={telHref(lead.phone) ?? undefined}>
                        {lead.phone}
                      </a>
                    ) : null
                  }
                />
                <ReadField label="Source" value={lead.source} />
                <ReadField label="Owner" value={owner?.name ?? owner?.email ?? '—'} />
                <ReadField label="NRE" value={fmtILS(lead.nreValue)} />
                <ReadField label="Yearly" value={fmtILS(lead.dealValueYearly)} accent="green" />
                <ReadField
                  label="Priority"
                  value={
                    <span className={`badge badge-${(lead.priority || 'B').toLowerCase()}`}>
                      {lead.priority}
                    </span>
                  }
                />
                <ReadField label="Created" value={fmtDate(lead.createdAt)} />
                {lead.tagIds && lead.tagIds.length > 0 && (
                  <ReadField
                    full
                    label="Tags"
                    value={
                      <div className="flex flex-wrap gap-1.5">
                        {lead.tagIds.map((tid) => {
                          const t = tagsAll.find((x) => x.id === tid);
                          if (!t) return null;
                          return (
                            <span
                              key={t.id}
                              className="badge"
                              style={{ background: `${t.color}22`, color: t.color }}
                            >
                              {t.name}
                            </span>
                          );
                        })}
                      </div>
                    }
                  />
                )}
                <button
                  type="button"
                  className={clsx(
                    'col-span-2 mt-1 p-3 rounded-md text-left border',
                    isOverdue(lead.nextActionDate)
                      ? 'bg-red/10 border-red/30'
                      : 'bg-surface2 border-border',
                  )}
                  onClick={() => setEditing(true)}
                  title="Tap to edit"
                >
                  <div className="text-[11px] text-text3 mb-0.5">Next Action</div>
                  <div className="font-medium">
                    {lead.nextAction || <span className="text-text3">Set a next action…</span>}
                  </div>
                  {lead.nextActionDate && (
                    <div
                      className={clsx(
                        'text-xs mt-0.5',
                        isOverdue(lead.nextActionDate) ? 'text-red' : 'text-text2',
                      )}
                    >
                      {isOverdue(lead.nextActionDate) ? '⏰ Overdue · ' : 'Due '}
                      {fmtDate(lead.nextActionDate)}
                    </div>
                  )}
                </button>
                {sortedCfs.map((f) => (
                  <ReadField
                    key={f.id}
                    label={f.label}
                    value={renderCustomFieldValue(f, (lead.customFields ?? {})[f.id])}
                  />
                ))}
                {lead.notes && (
                  <ReadField
                    full
                    label="Notes"
                    value={
                      <div className="whitespace-pre-wrap text-text2 break-words">{lead.notes}</div>
                    }
                  />
                )}
              </div>
            )}
          </Section>

          <Section
            title="Activity log"
            right={
              <button className="btn btn-sm btn-primary" onClick={() => setLogSheet({})}>
                + Add activity
              </button>
            }
          >
            {acts.length === 0 && <div className="text-text3 text-sm p-2">No activities yet</div>}
            {acts.map((a) => (
              <div
                key={a.id}
                className="bg-surface2 rounded-md p-3 mb-2 border-l-[3px] border-accent"
              >
                <div className="flex justify-between gap-2 text-xs text-text2 mb-1">
                  <span className="truncate">
                    <span className="text-accent2 font-semibold">{a.typeName}</span> ·{' '}
                    {a.loggedByName ?? '—'}
                  </span>
                  <span className="flex gap-2 items-center flex-shrink-0">
                    {fmtDate(a.date)}
                    <button
                      className="text-text3 hover:text-red px-1.5 -mr-1.5 text-base leading-none"
                      aria-label="Delete activity"
                      onClick={() => {
                        if (confirm('Delete this activity?')) delAct.mutate(a.id);
                      }}
                    >
                      ×
                    </button>
                  </span>
                </div>
                <div className="text-sm whitespace-pre-wrap break-words">{a.summary}</div>
              </div>
            ))}
          </Section>

          <AttachmentsSection leadId={lead.id} attachments={atts} onUploaded={() => undefined} />

          <Section title="Audit history">
            {audit.length === 0 && <div className="text-text3 text-sm">Nothing yet.</div>}
            <div className="space-y-1.5">
              {audit.map((a) => (
                <div
                  key={a.id}
                  className="text-xs bg-surface2 rounded-md px-3 py-2 flex flex-wrap gap-x-3 gap-y-0.5"
                >
                  <span className="text-text3 w-20 flex-shrink-0">{fmtDate(a.createdAt)}</span>
                  <span className="text-text2 flex-shrink-0 truncate max-w-[40%]">
                    {a.userName ?? '—'}
                  </span>
                  <span className="text-accent2 font-medium">{a.action}</span>
                </div>
              ))}
            </div>
          </Section>

          {!editing && (
            <div className="mt-6 pt-4 border-t border-border flex justify-end">
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (confirm(`Delete ${lead.company}?`)) {
                    remove.mutate(lead.id, { onSuccess: onClose });
                  }
                }}
              >
                Delete lead
              </button>
            </div>
          )}
        </div>

        {/* ---- Sticky save bar while editing (sits above the home indicator; on
             Android the viewport shrinks for the keyboard so it stays reachable) ---- */}
        {editing && (
          <div className="flex-shrink-0 border-t border-border bg-surface px-3 md:px-6 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex gap-2 justify-end">
            <button className="btn btn-secondary flex-1 md:flex-none" onClick={cancelEdit}>
              Cancel
            </button>
            <button
              className="btn btn-primary flex-1 md:flex-none"
              onClick={saveEdit}
              disabled={update.isPending}
            >
              {update.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </div>
      {logSheet && (
        <LogActivitySheet
          lead={lead}
          stages={sortedStages}
          types={types}
          initialTypeName={logSheet.typeName}
          title={logSheet.title}
          onClose={() => setLogSheet(null)}
          onStageChange={changeStage}
        />
      )}
      {pendingTerminal && (
        <WonLostPrompt
          leadId={lead.id}
          targetStage={pendingTerminal}
          onClose={() => setPendingTerminal(null)}
          onDone={() => {
            setPendingTerminal(null);
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

function ContactActions({
  lead,
  onContact,
}: {
  lead: Lead;
  onContact: (kind: ContactKind) => void;
}) {
  const tel = telHref(lead.phone);
  const wa = whatsappHref(lead.phone, lead.contactName ? `Hi ${lead.contactName}, ` : undefined);
  const mail = lead.email ? `mailto:${lead.email}` : null;
  if (!tel && !wa && !mail) return null;
  // On phones these are the primary actions, so they fill the row with large
  // thumb-sized targets; on desktop they stay compact.
  const cls = 'btn btn-sm btn-secondary flex-1 md:flex-none';
  return (
    <div className="flex gap-2 mb-3 md:mb-4 flex-wrap">
      {tel && (
        <a className={cls} href={tel} onClick={() => onContact('Call')}>
          📞 Call
        </a>
      )}
      {wa && (
        <a
          className={cls}
          href={wa}
          target="_blank"
          rel="noreferrer"
          onClick={() => onContact('WhatsApp')}
        >
          💬 WhatsApp
        </a>
      )}
      {mail && (
        <a className={cls} href={mail} onClick={() => onContact('Email')}>
          ✉️ Email
        </a>
      )}
    </div>
  );
}

function StageBar({
  stage,
  stages,
  stageEnteredAt,
  onChange,
  busy,
}: {
  stage?: Stage;
  stages: Stage[];
  stageEnteredAt: string;
  onChange: (stageId: string) => void;
  busy: boolean;
}) {
  const inStage = daysSince(stageEnteredAt);
  const slaBreached = !!stage?.slaDays && inStage > stage.slaDays;
  return (
    <div className="flex items-center gap-2 mb-3 md:mb-4">
      <label className="text-xs text-text3 flex-shrink-0" htmlFor="lead-stage-select">
        Stage
      </label>
      <div className="relative flex-1 min-w-0">
        <span
          className="absolute left-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full pointer-events-none"
          style={{ background: stage?.color ?? '#666' }}
        />
        <select
          id="lead-stage-select"
          value={stage?.id ?? ''}
          disabled={busy}
          onChange={(e) => onChange(e.target.value)}
          className="pl-7 font-medium"
        >
          {stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      {stage && (
        <span
          className={clsx(
            'text-[11px] whitespace-nowrap flex-shrink-0',
            slaBreached ? 'px-2 py-0.5 rounded bg-red/15 text-red' : 'text-text3',
          )}
          title={
            slaBreached ? `Past ${stage.slaDays}-day SLA in this stage` : 'Days in current stage'
          }
        >
          {inStage}d{stage.probability ? ` · ${stage.probability}%` : ''}
        </span>
      )}
    </div>
  );
}

function QuickLogBar({
  types,
  onLog,
  disabled,
}: {
  types: { id: string; name: string; order?: number }[];
  onLog: (typeName: string) => void;
  disabled: boolean;
}) {
  const names = types.length
    ? types
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((t) => t.name)
    : ['Call', 'WhatsApp', 'Email', 'Meeting', 'Note'];
  return (
    <div className="mb-4 p-2.5 md:p-3 bg-surface2 border border-border rounded-md">
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
        <span className="text-xs text-text3 mr-1 flex-shrink-0">Log:</span>
        {names.map((t) => (
          <button
            key={t}
            className="btn btn-sm btn-secondary flex-shrink-0"
            onClick={() => onLog(t)}
            disabled={disabled}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
  right,
}: {
  title: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2 gap-2">
        <h3 className="text-xs font-semibold text-text2 uppercase tracking-wide">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="block text-[11px] text-text3 mb-1 font-medium">{label}</label>
      {children}
    </div>
  );
}

function ReadField({
  label,
  value,
  full,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  full?: boolean;
  accent?: 'green';
}) {
  return (
    <div className={clsx('min-w-0', full && 'col-span-2')}>
      <label className="block text-[11px] text-text3 mb-0.5 font-medium">{label}</label>
      <div className={clsx('break-words', accent === 'green' && 'text-green')}>
        {value ?? <span className="text-text3">—</span>}
      </div>
    </div>
  );
}

function CustomFieldEditor({
  field,
  value,
  onChange,
}: {
  field: CustomField;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (field.type === 'select') {
    return (
      <Field label={field.label}>
        <select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </Field>
    );
  }
  return (
    <Field label={field.label}>
      <input
        type={
          field.type === 'date'
            ? 'date'
            : field.type === 'number'
              ? 'number'
              : field.type === 'url'
                ? 'url'
                : field.type === 'email'
                  ? 'email'
                  : 'text'
        }
        inputMode={
          field.type === 'number'
            ? 'decimal'
            : field.type === 'url'
              ? 'url'
              : field.type === 'email'
                ? 'email'
                : undefined
        }
        autoCapitalize={field.type === 'url' || field.type === 'email' ? 'none' : undefined}
        value={value == null ? '' : String(value)}
        onChange={(e) =>
          onChange(
            field.type === 'number' && e.target.value !== ''
              ? Number(e.target.value)
              : e.target.value,
          )
        }
      />
    </Field>
  );
}

function renderCustomFieldValue(field: CustomField, raw: unknown): React.ReactNode {
  if (raw == null || raw === '') return <span className="text-text3">—</span>;
  if (field.type === 'date') return fmtDate(String(raw));
  if (field.type === 'url')
    return (
      <a href={String(raw)} target="_blank" rel="noreferrer" className="text-accent2 break-all">
        {String(raw)}
      </a>
    );
  if (field.type === 'email')
    return (
      <a href={`mailto:${String(raw)}`} className="text-accent2 break-all">
        {String(raw)}
      </a>
    );
  if (field.type === 'select')
    return <span className="badge bg-surface3 text-text">{String(raw)}</span>;
  return <span className="text-text2">{String(raw)}</span>;
}

function TagPicker({
  available,
  selected,
  onChange,
}: {
  available: { id: string; name: string; color: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {available.map((t) => {
        const on = selected.includes(t.id);
        return (
          <button
            key={t.id}
            className="badge !py-1.5 !px-3 !text-xs"
            type="button"
            style={
              on
                ? { background: t.color, color: '#fff' }
                : { background: 'transparent', color: t.color, border: `1px solid ${t.color}55` }
            }
            onClick={() => onChange(on ? selected.filter((x) => x !== t.id) : [...selected, t.id])}
          >
            {t.name}
          </button>
        );
      })}
      {available.length === 0 && (
        <span className="text-text3 text-xs">No tags yet — add some in Settings.</span>
      )}
    </div>
  );
}

function AttachmentsSection({
  leadId,
  attachments,
}: {
  leadId: string;
  attachments: {
    id: string;
    fileName: string;
    contentType?: string;
    size: number;
    downloadUrl?: string;
    createdAt: string;
  }[];
  onUploaded?: () => void;
}) {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadAttachment(leadId, file);
      qc.invalidateQueries({ queryKey: ['attachments', leadId] });
    } catch (err) {
      alert((err as Error).message);
    }
    setUploading(false);
    e.target.value = '';
  };
  const remove = async (id: string) => {
    if (!confirm('Delete attachment?')) return;
    await api(`/api/attachments/${id}`, { method: 'DELETE' });
    qc.invalidateQueries({ queryKey: ['attachments', leadId] });
  };
  return (
    <Section
      title="Attachments"
      right={
        <label className="btn btn-sm btn-secondary cursor-pointer">
          {uploading ? 'Uploading…' : '+ Upload'}
          {/* No `accept` on purpose: phones then offer camera, photo library and files. */}
          <input type="file" hidden onChange={onPick} disabled={uploading} />
        </label>
      }
    >
      {attachments.length === 0 && <div className="text-text3 text-sm">No attachments.</div>}
      <div className="space-y-1.5">
        {attachments.map((a) => (
          <div
            key={a.id}
            className="bg-surface2 rounded-md px-3 py-2 flex items-center gap-3 text-sm"
          >
            <a
              href={a.downloadUrl}
              className="text-accent2 truncate flex-1 py-1"
              target="_blank"
              rel="noreferrer"
            >
              {a.contentType?.startsWith('audio/') ? '▶ ' : ''}
              {a.fileName}
            </a>
            <span className="text-text3 text-xs flex-shrink-0">
              {(a.size / 1024).toFixed(0)} KB
            </span>
            <button
              className="text-text3 hover:text-red px-2 -mr-2 text-base leading-none"
              onClick={() => remove(a.id)}
              aria-label="Delete attachment"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}
